import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { searchSites } from '../../services/sites';
import { Col, Form, Row } from 'react-bootstrap';
import CustomAsyncSelect from '../form_components/CustomAsyncSelect';
import CustomButton from '../shared/CustomButton';
import { FaSave } from 'react-icons/fa';

const CheckpointForm = ({
    siteLocation: checkpoint,
    isSaving,
    formAction
}) => {
    const {
        register,
        control,
        reset,
        handleSubmit,
        formState: {errors},
        setError
    } = useForm({ defaultValues: checkpoint ?
        {
            id: checkpoint.id,
            site: {
                label: checkpoint.site.name,
                value: checkpoint.site.id
            },
            name: checkpoint.name,
            geo_fence_radius: checkpoint.geo_fence_radius,
            latitude: checkpoint.latitude,
            longitude: checkpoint.longitude,
            rfid_tag_id: checkpoint.rfid_tag_id
        } : {}
    });

    const resetForm = () => {
        reset({
            id: '',
            site: null,
            name: '',
            geo_fence_radius: '',
            latitude: '',
            longitude: '',
            rfid_tag_id: ''
        });
    }

    useEffect(() => {
        if (checkpoint) {
            reset({
                id: checkpoint.id,
                site: {
                    label: checkpoint.site.name,
                    value: checkpoint.site.id
                },
                name: checkpoint.name,
                geo_fence_radius: checkpoint.geo_fence_radius,
                latitude: checkpoint.latitude,
                longitude: checkpoint.longitude,
                rfid_tag_id: checkpoint.rfid_tag_id
            });
        } else {
            reset({
                id: '',
                site: null,
                name: '',
                geo_fence_radius: '',
                latitude: '',
                longitude: '',
                rfid_tag_id: ''
            });
        }
    }, [reset, checkpoint]);

    /**
     * search for sites using name 
     * @param {} inputValue 
     * @returns 
     */
    const loadSites = async (inputValue) => {
        if(inputValue.length < 3){
            return []
        } 

        try {
            const response = await searchSites(inputValue);
            return response.data.map((site) => ({label: site.name, value: site.id, ...site}));
        } catch {
            return [];
        }
    }

    const onSubmit = async (data) => {
        try {
            const success = await formAction(data);
            if (success){
                resetForm();
            }
        } catch (error) {
            console.log(error.response.data.errors);
            if (error.response && error.response.status === 422){
                Object.keys(error.response.data.errors).forEach((field) => {
                    setError(field, {
                        type: 'manual',
                        message: error.response.data.errors[field][0]
                    });
                });
            } else {
                throw error; // error to be handled in saveSiteLocation in SiteLocations.jsx 
            }
        }
    }

  return (
   <Form onSubmit={handleSubmit(onSubmit)}>

        <Row>

            <Col xl={3} lg={4} md={6} sm={12}>

                <Form.Group>

                    <Form.Label htmlFor='site'>Site</Form.Label>
                    <CustomAsyncSelect
                        name='site'
                        loadOptions={loadSites}
                        placeholder='Select Site'
                        control={control}
                        rules={{ required: 'Select Site'}}
                    />
                    {errors.site_id && (
                        <div className="invalid-feedback">{errors.site_id.message}</div>
                    )}

                </Form.Group>

            </Col>

            <Col xl={3} lg={4} md={6} sm={12}>
                    
                <Form.Group>

                    <Form.Label htmlFor='name'>Name</Form.Label>
                    <input type='text' autoComplete='off' placeholder='Name'
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        {...register('name', {
                            required: 'Enter checkpoint name',
                        })}
                    />
                    {errors.name && (
                        <div className="invalid-feedback">{errors.name.message}</div>
                    )}

                </Form.Group>

            </Col>

            <Col xl={3} lg={4} md={6} sm={12}>
                
                <Form.Group>

                    <Form.Label htmlFor='geo_fence_radius'>Geofence Radius(m)</Form.Label>
                    <input type='number' step='0.1' autoComplete='off' placeholder='Geofence Radius(m)'
                        className={`form-control ${errors.geo_fence_radius ? 'is-invalid' : ''}`}
                        {...register('geo_fence_radius', {
                            required: 'Enter location geofence radius',
                            pattern: {
                                // Alternative: Requires at least one digit before the decimal point (e.g., '0.5' but not '.5')
                                value: /^\d+(\.\d+)?$/,
                                message: 'Invalid value for geofence radius. Enter a whole number or a decimal (e.g., 100 or 50.5)'
                            },
                            min: {
                                value: 0,
                                message: 'Geo fence radius must be a non-negative integer',
                            },
                        })}
                    />
                    {errors.geo_fence_radius && (
                        <div className="invalid-feedback">{errors.geo_fence_radius.message}</div>
                    )}
                    
                </Form.Group>

            </Col>

            <Col xl={3} lg={4} md={6} sm={12}>
                            
                <Form.Group>

                    <Form.Label htmlFor='latitude'>Latitude</Form.Label>
                    <input type='number' autoComplete='off' placeholder='Latitude' step='any'
                        className={`form-control ${errors.latitude ? 'is-invalid' : ''}`}
                        {...register('latitude', {
                            required: 'Enter checkpoint\'s latitude coordinate',
                            valueAsNumber: true,
                            min: {
                                value: -90,
                                message: 'Latitude must be between -90 and 90'
                            },
                            max: {
                                value: 90,
                                message: 'Latitude must be between -90 and 90'
                            }
                        })}
                    />
                    {errors.latitude && (
                        <div className="invalid-feedback">{errors.latitude.message}</div>
                    )}

                </Form.Group>
            
            </Col>

        </Row>

        <Row>

            <Col xl={3} lg={4} md={6} sm={12}>
            
                <Form.Group>
                
                    <Form.Label  htmlFor='longitude'>Longitude</Form.Label>
                    <input type='number' autoComplete='off' placeholder='Longitude' step='any'
                        className={`form-control ${errors.longitude ? 'is-invalid' : ''}`}
                        {...register('longitude', {
                            required: 'Enter checkpoint longitude coordinate',
                            valueAsNumber: true,
                            max: {
                                value: 180,
                                message: 'Longitude must be between -180 and 180'
                            },
                            min: {
                                value: -180,
                                message: 'Longitude must be between -180 and 180'
                            }
                        })}
                    />
                    {errors.longitude && (
                        <div className="invalid-feedback">{errors.longitude.message}</div>
                    )}
                
                </Form.Group>
        
            </Col>

            <Col xl={3} lg={4} md={6} sm={12}>

                <Form.Group>

                    <Form.Label htmlFor='rfid_tag_id'>RFID Serial Number</Form.Label>
                    <input type='text' placeholder='RFID Serial Number' autoComplete='off'
                        className={`form-control ${errors.rfid_tag_id ? 'is-invalid' :''}`}
                        {...register('rfid_tag_id', {
                            required: 'Enter checkpoint rfid serial number',
                            maxLength: {
                                value: 30,
                                message: 'RFID Serial Number must not have more than 30 characters.'
                            },
                            minLength: {
                                value: 10,
                                message: 'RFID Serial Number must have at least 10 characters'
                            }
                        })}
                    />
                    {errors.rfid_tag_id && (
                        <div className="invalid-feedback">{errors.rfid_tag_id.message}</div>
                    )}

                </Form.Group>
            
            </Col>

        </Row>

        <CustomButton
            title={isSaving ? 'Saving' : 'Save'}
            isSaving={isSaving}
            icon={<FaSave />}
        />

   </Form>
  )
}

export default CheckpointForm